/**
 * Personal access tokens — DESIGN.md §12.5.
 *
 * "**Settings › API & Agents:** create token (name — e.g. 'Claude Code —
 * Brad's laptop', scope **read** or **read-write**, optional expiry). The
 * `slate_pat_…` secret displays exactly once; sha256 stored … with a prefix
 * retained for the token list; last-used tracked; owner-revocable, admins see
 * and revoke all (audited)."
 *
 * The response type deliberately has no field for the secret. `secret` exists
 * only on the creation response, so no later read can leak it — the type
 * system says what §12.5 says.
 */
import { z } from 'zod';
import { TOKEN_SCOPES } from '../enums.js';
import { name, uuidSchema } from './common.js';
import { userRefSchema } from './users.js';
export const apiTokenSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    /** `slate_pat_ab12` — enough to tell two tokens apart, never enough to use. */
    prefix: z.string(),
    scopes: z.array(z.enum(TOKEN_SCOPES)),
    /** Captured from the MCP `initialize` handshake, once the agent connects. */
    clientName: z.string().nullable(),
    lastUsedAt: z.string().nullable(),
    expiresAt: z.string().nullable(),
    revokedAt: z.string().nullable(),
    createdAt: z.string(),
    /** Whose token this is. Admins list everyone's (§12.5). */
    owner: userRefSchema,
});
/**
 * The one response that carries the secret — §12.5's "displays exactly once".
 */
export const createdApiTokenSchema = apiTokenSchema.extend({
    secret: z.string(),
});
export const createApiTokenRequestSchema = z
    .object({
    name: name(120),
    /**
     * §12.5 — "scope **read** or **read-write**". A read token gets `['read']`;
     * read-write gets both, because every write path also reads.
     */
    scope: z.enum(['read', 'write']).default('read'),
    /** `YYYY-MM-DD`, optional — §12.5's "optional expiry". */
    expiresOn: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullish(),
})
    .strict();
//# sourceMappingURL=tokens.js.map